import { registerSW } from "virtual:pwa-register";

export interface PwaState {
  canInstall: boolean;
  offlineReady: boolean;
  updateReady: boolean;
}

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let installPrompt: InstallPromptEvent | null = null;
const listeners = new Set<(state: PwaState) => void>();
const state: PwaState = { canInstall: false, offlineReady: false, updateReady: false };
let updateServiceWorker: ((reloadPage?: boolean) => Promise<void>) | undefined;

const emit = (): void => listeners.forEach((listener) => listener({ ...state }));

export function initializePwa(): void {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    installPrompt = event as InstallPromptEvent;
    state.canInstall = true;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    installPrompt = null;
    state.canInstall = false;
    emit();
  });
  updateServiceWorker = registerSW({
    immediate: true,
    onOfflineReady: () => { state.offlineReady = true; emit(); },
    onNeedRefresh: () => { state.updateReady = true; emit(); },
    onRegisterError: (error) => console.warn("Service worker registration failed", error),
  });
}

export const subscribePwa = (listener: (value: PwaState) => void): (() => void) => {
  listeners.add(listener);
  listener({ ...state });
  return () => listeners.delete(listener);
};

export async function promptInstall(): Promise<boolean> {
  if (!installPrompt) return false;
  await installPrompt.prompt();
  const choice = await installPrompt.userChoice;
  if (choice.outcome === "accepted") {
    installPrompt = null;
    state.canInstall = false;
    emit();
    return true;
  }
  return false;
}

export async function applyUpdate(): Promise<void> {
  await updateServiceWorker?.(true);
}

export async function requestFullscreenAndLandscape(): Promise<void> {
  try {
    if (!document.fullscreenElement) await document.documentElement.requestFullscreen?.({ navigationUI: "hide" });
  } catch { /* Fullscreen needs a direct gesture in some browsers. */ }
  try {
    await screen.orientation?.lock?.("landscape");
  } catch { /* iOS and browser tabs commonly disallow locking. */ }
}
